import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, forwardRef } from "react";
import logoDark from "@/assets/logo-dark.png";
import logoWhite from "@/assets/logo-white.png";
import go24SubLogoLarge from "@/assets/go24-sub-logo-large.png";

interface LogoProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  variant?: "dark" | "white" | "auto" | "sub";
}

export const Logo = forwardRef<HTMLButtonElement, LogoProps>(
  ({ className = "", size = "md", variant = "auto" }, ref) => {
    const navigate = useNavigate();
    const [dashboardPath, setDashboardPath] = useState("/");

    useEffect(() => {
      checkUserRole();
    }, []);

    const checkUserRole = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setDashboardPath("/"); return; }

      const [{ data: roles }, { data: isSuperAdmin }] = await Promise.all([
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id),
        supabase.rpc("is_super_admin", { _user_id: session.user.id }),
      ]);

      if (roles?.some(r => r.role === "admin")) {
        if (isSuperAdmin) {
          setDashboardPath("/admin");
          return;
        }
        const { data: assignment } = await supabase
          .from("admin_assignments")
          .select("admin_type")
          .eq("user_id", session.user.id)
          .eq("is_active", true)
          .maybeSingle();
        if (assignment?.admin_type === "dispatch") setDashboardPath("/d");
        else if (assignment?.admin_type === "franchisee") setDashboardPath("/p");
        else setDashboardPath("/admin");
      } else if (roles?.some(r => r.role === "supplier" || r.role === "provider")) {
        setDashboardPath("/s");
      } else if (roles?.some(r => r.role === "customer")) {
        setDashboardPath("/c");
      } else {
        setDashboardPath("/");
      }
    };

    const sizeClasses = { sm: "h-8", md: "h-10", lg: "h-16", xl: "h-24" };

    if (variant === "sub") {
      return (
        <button
          ref={ref}
          onClick={() => navigate(dashboardPath)}
          className={`hover:opacity-80 transition-opacity ${className}`}
        >
          <img src={go24SubLogoLarge} alt="Go24" className={`${sizeClasses[size]} w-auto object-contain`} />
        </button>
      );
    }

    const logoSrc = variant === "white" ? logoWhite : logoDark;

    return (
      <button
        ref={ref}
        onClick={() => navigate(dashboardPath)}
        className={`hover:opacity-80 transition-opacity ${className}`}
      >
        <img src={logoSrc} alt="Go24" className={`${sizeClasses[size]} w-auto object-contain`} />
      </button>
    );
  }
);

Logo.displayName = "Logo";
export default Logo;
